/**
 * Button — canonical action-button primitive.
 *
 * Replaces ~350 ad-hoc `<button>` className strings across the app. One
 * component, five intents, three sizes, consistent hover / disabled /
 * focus states, and a shared icon slot.
 *
 *   <Button variant="primary" onClick={save}>Save</Button>
 *   <Button variant="secondary" icon={<Plus />}>Add row</Button>
 *   <Button variant="ai" icon={<Sparkles />}>Suggest links</Button>
 *   <Button variant="ghost" iconOnly aria-label="Close"><X /></Button>
 *
 * Intents:
 *   primary       brand-filled CTA (Save, Submit, Create)
 *   secondary     border + white bg (Cancel, Reset to defaults)
 *   ghost         transparent bg + hover tint (icon-only, list-row affordances)
 *   destructive   red-50 bg + red-700 text (Delete, Remove, Reject)
 *   ai            blue-50 bg + blue-700 text (Sparkles affordances, AI insights)
 *
 * Pass `asChild` if you need a non-button element (e.g. `<a>`) with the
 * same styling — pattern intentionally not implemented yet; if you find
 * yourself wanting it, swap in radix Slot.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'ai'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Optional leading icon. Pair with text for clarity. */
  icon?: ReactNode
  /** Optional trailing icon. Use sparingly — usually for "Continue →" patterns. */
  trailingIcon?: ReactNode
  /** True if the button has no visible text — applies square padding. */
  iconOnly?: boolean
  /** Render as a full-width block within its container. */
  fullWidth?: boolean
}

const SIZE_CLS: Record<ButtonSize, { base: string; iconOnly: string; iconGap: string; iconSize: number }> = {
  sm: { base: 'h-8 px-2.5 text-[12px]', iconOnly: 'h-8 w-8',  iconGap: 'gap-1',   iconSize: 12 },
  md: { base: 'h-9 px-3 text-body-sm',  iconOnly: 'h-9 w-9',  iconGap: 'gap-1.5', iconSize: 14 },
  lg: { base: 'h-10 px-4 text-body-md', iconOnly: 'h-10 w-10', iconGap: 'gap-2',   iconSize: 16 },
}

const VARIANT_CLS: Record<ButtonVariant, string> = {
  // !text-white + [&>svg]:!text-white force-override any inherited or
  // child-element color so the text and the leading/trailing Lucide icon
  // both render white on the brand-blue background. Caught in production-
  // readiness E2E (test 1.2) — text was rendering dark on some browsers.
  primary:
    'bg-brand-500 !text-white [&>svg]:!text-white hover:bg-brand-600 disabled:bg-slate-300 disabled:!text-white/80',
  secondary:
    'border border-border bg-white text-text-secondary hover:bg-surface-muted hover:text-text-primary disabled:opacity-50',
  ghost:
    'text-text-secondary hover:bg-surface-muted hover:text-text-primary disabled:opacity-40',
  destructive:
    'border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-300 disabled:opacity-50',
  ai:
    'border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 disabled:opacity-60',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon,
    trailingIcon,
    iconOnly = false,
    fullWidth = false,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const sizeCls = SIZE_CLS[size]
  const base =
    'inline-flex items-center justify-center font-medium rounded-lg transition-colors whitespace-nowrap ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 ' +
    'disabled:cursor-not-allowed'

  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        base,
        VARIANT_CLS[variant],
        iconOnly ? sizeCls.iconOnly : sizeCls.base,
        !iconOnly && sizeCls.iconGap,
        // Touch-target floor on phones only (≥44px) — desktop density unchanged.
        'max-md:min-h-[44px]',
        iconOnly && 'max-md:min-w-[44px]',
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {icon}
      {/* For iconOnly buttons we still render children when no `icon` prop
          was provided — caller may pass the icon as children directly. */}
      {iconOnly ? (icon ? null : children) : children}
      {trailingIcon}
    </button>
  )
})
